import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import * as azureReq from '../../axios/requests/azureReq';
import './AzureCallback.css';

const AzureCallback = () => {
    const [t] = useTranslation('common');
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [message, setMessage] = useState('');
    const isProcessingRef = useRef(false); // Prevent duplicate calls in React 18 Strict Mode

    useEffect(() => {
        const handleCallback = async () => {
            // Prevent duplicate execution
            if (isProcessingRef.current) return;
            isProcessingRef.current = true;

            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');
            const errorDescription = searchParams.get('error_description');

            // Handle error from Microsoft
            if (error) {
                setStatus('error');
                setMessage(errorDescription || error);

                if (window.opener) {
                    window.opener.postMessage({
                        type: 'AZURE_AUTH_ERROR',
                        error: error,
                        description: errorDescription || t('Authentication failed')
                    }, window.location.origin);
                }

                setTimeout(() => window.close(), 2000);
                return;
            }

            // Handle successful auth code
            if (code && state) {
                try {
                    setMessage(t('Completing authentication...'));
                    const response = await azureReq.oauthCallback(code, state);
                    const data = response.data || response;

                    setStatus('success');
                    setMessage(t('Account connected successfully!'));

                    if (window.opener) {
                        window.opener.postMessage({
                            type: 'AZURE_AUTH_SUCCESS',
                            email: data.email,
                            accountId: data.accountId,
                            displayName: data.displayName
                        }, window.location.origin);
                    }

                    setTimeout(() => window.close(), 1500);
                } catch (err) {
                    setStatus('error');
                    const errorMsg = err.response?.data?.message || err.message || t('Failed to complete authentication');
                    setMessage(errorMsg);

                    if (window.opener) {
                        window.opener.postMessage({
                            type: 'AZURE_AUTH_ERROR',
                            error: 'callback_failed',
                            description: errorMsg
                        }, window.location.origin);
                    }

                    setTimeout(() => window.close(), 2000);
                }
            } else {
                setStatus('error');
                setMessage(t('Missing authentication parameters'));

                if (window.opener) {
                    window.opener.postMessage({
                        type: 'AZURE_AUTH_ERROR',
                        error: 'missing_params',
                        description: t('Missing code or state parameter')
                    }, window.location.origin);
                }

                setTimeout(() => window.close(), 2000);
            }
        };

        handleCallback();
    }, [searchParams, t]);

    return (
        <div className="azure-callback-container">
            <div className="azure-callback-card">
                <div className="azure-callback-header">
                    <img
                        src="https://img.icons8.com/fluency/96/microsoft-365.png"
                        alt="Microsoft 365"
                        className="azure-callback-logo"
                    />
                    <h2 className="azure-callback-title">Microsoft 365</h2>
                </div>

                <div className="azure-callback-content">
                    {status === 'loading' && (
                        <>
                            <div className="azure-callback-spinner"></div>
                            <p className="azure-callback-message">
                                {message || t('Processing authentication...')}
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <FiCheckCircle className="azure-callback-icon azure-callback-icon--success" />
                            <p className="azure-callback-message azure-callback-message--success">
                                {message}
                            </p>
                            <p className="azure-callback-submessage">
                                {t('This window will close automatically...')}
                            </p>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <FiXCircle className="azure-callback-icon azure-callback-icon--error" />
                            <p className="azure-callback-message azure-callback-message--error">
                                {t('Authentication Failed')}
                            </p>
                            <p className="azure-callback-error-details">{message}</p>
                            <p className="azure-callback-submessage">
                                {t('This window will close automatically...')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AzureCallback;
